import { BankIcon } from "@phosphor-icons/react/dist/csr/Bank";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { MoneyInput } from "@volca/ui/components/ui/money-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { parseMaskedDollarInput } from "@/core/utils/money";
import {
  useDepositWithdraw,
  useGetAchAccount,
} from "@/internal-transactions/query";
import volcaBlackWhite from "@/assets/images/volca-black-white.png";

const schema = Yup.object().shape({
  amount: Yup.number()
    .transform((_, orig) => {
      if (orig === "" || orig === null || orig === undefined) return undefined;
      const parsed = parseMaskedDollarInput(orig);
      return parsed !== null ? parsed : undefined;
    })
    .required("Amount is required")
    .positive("Amount must be positive")
    .min(1, "Amount must be at least 1"),
});

interface DepositWithdrawModalProps {
  mode?: "deposit" | "withdraw";
  onOpenChange: (open: boolean) => void;
  businessId?: string | null;
}

const DepositWithdrawModal = ({
  mode,
  onOpenChange,
  businessId,
}: DepositWithdrawModalProps) => {
  const isDeposit = mode === "deposit";

  const { data: achAccount } = useGetAchAccount(businessId);
  const { mutate: depositWithdraw } = useDepositWithdraw(isDeposit, businessId);

  return (
    <Dialog open={!!mode} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogDescription className="sr-only">
          {isDeposit ? "Deposit funds" : "Withdraw funds"}
        </DialogDescription>
        <Formik
          initialValues={{
            amount: "",
          }}
          validationSchema={schema}
          validateOnMount={true}
          onSubmit={(values, { setSubmitting }) => {
            const parsed = parseMaskedDollarInput(String(values.amount ?? ""));
            const amountCents = parsed !== null ? Math.round(parsed * 100) : 0;

            return depositWithdraw(
              { amount: amountCents },
              {
                onSuccess: () => {
                  toast(
                    `${isDeposit ? "Deposit" : "Withdrawal"} successfully initiated`,
                  );
                  setSubmitting(false);
                  onOpenChange(false);
                },
              },
            );
          }}
        >
          {({
            isSubmitting,
            isValid,
            errors,
            touched,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <Form className="grid gap-4">
              <DialogHeader>
                <DialogTitle>
                  {isDeposit ? "Deposit funds" : "Withdraw funds"}
                </DialogTitle>
              </DialogHeader>

              <Stack align="start" className="w-full" gap={6}>
                <Stack align="start" gap={6}>
                  <Stack direction="row" gap={4} align="center">
                    {isDeposit ? (
                      <BankIcon className="h-6 w-6 text-subtle" />
                    ) : (
                      <img
                        src={volcaBlackWhite}
                        alt="Volca"
                        className="h-6 w-6"
                      />
                    )}
                    <Stack align="start" gap={1}>
                      <Text variant="label" tone="muted">
                        From
                      </Text>
                      <Text>
                        {isDeposit
                          ? `${achAccount?.name} ending in ${achAccount?.mask}`
                          : "Volca"}
                      </Text>
                    </Stack>
                  </Stack>

                  <Stack direction="row" gap={4} align="center">
                    {isDeposit ? (
                      <img
                        src={volcaBlackWhite}
                        alt="Volca"
                        className="h-6 w-6"
                      />
                    ) : (
                      <BankIcon className="h-6 w-6 text-subtle" />
                    )}
                    <Stack align="start" gap={1}>
                      <Text variant="label" tone="muted">
                        To
                      </Text>
                      <Text>
                        {isDeposit
                          ? "Volca"
                          : `${achAccount?.name} - ${achAccount?.mask}`}
                      </Text>
                    </Stack>
                  </Stack>
                </Stack>

                <MoneyInput
                  label={`Amount to ${isDeposit ? "deposit" : "withdraw"}`}
                  error={
                    touched.amount && values.amount ? errors.amount : undefined
                  }
                  name="amount"
                  value={values.amount || ""}
                  onChange={(value) => setFieldValue("amount", value)}
                  onBlur={() => setFieldTouched("amount", true)}
                  required
                  fullWidth
                  autoFocus
                />

                <Text tone="muted">
                  Your funds will arrive in 3-5 business days
                </Text>
              </Stack>

              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  loading={isSubmitting}
                  className="min-w-[108px]"
                >
                  {isDeposit ? "Deposit" : "Withdraw"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default DepositWithdrawModal;
